import React, { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import { useCookies } from 'react-cookie';
import { useTranslation } from 'react-i18next';
import CSS from 'csstype';
//* MUI
import { Close, Language } from '@mui/icons-material';
import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  FilterOptionsState,
  IconButton,
  InputAdornment,
  InputLabel,
  Paper,
  TextField,
  Theme
} from '@mui/material';
//* Components
import { Transition } from '../../Common/Dialogs/DialogBox';
//* Models
import { DepositDetailsModel } from '../../../models/profile';
//* Utils
import { colors } from '../../../utils/theme';
import { countries, CountryType } from '../../../utils/countries';
import { getBorderColor, getIconColor } from '../../../utils/ui';
//* Styles
import './ProfileDetailsPopup.scss';

type IErrors = {
  firstName: string | null;
  lastName: string | null;
  phoneCode: string | null;
  phone: string | null;
  country: string | null;
  address: string | null;
  city: string | null;
  postCode: string | null;
};

const defaultErrorsObj: IErrors = {
  firstName: null,
  lastName: null,
  phoneCode: null,
  phone: null,
  country: null,
  address: null,
  city: null,
  postCode: null
};

export const labelStyle: CSS.Properties = {
  top: '10px',
  color: colors.primary.main,
  textTransform: 'uppercase',
  fontWeight: 600,
  fontSize: '14px',
  fontFamily: 'Poppins'
};

export const inputPropsStyle = {
  height: '22px !important',
  padding: '12px !important',
  '&:-webkit-autofill': {
    WebkitTextFillColor: colors.info.main
  }
};

const filterOptions = (options: CountryType[], state: FilterOptionsState<CountryType>) => {
  let searchValue = state.inputValue;
  if (searchValue === '') {
    return options;
  } else if (searchValue.length > 0 && searchValue[0] === '+') {
    searchValue = state.inputValue.substring(1);
  }
  const result = options.filter(
    (opt) =>
      opt.label.toLowerCase().indexOf(searchValue.toLowerCase()) === 0 ||
      opt.phone.indexOf(searchValue) === 0
  );
  return result;
};
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
//@ts-ignore
const CustomPaper = (props) => {
  return <Paper elevation={8} {...props} style={{ width: 300 }} />;
};

interface IProps {
  open: boolean;
  onClose: () => void;
  playerDetails: DepositDetailsModel;
  setPlayerDetails: Dispatch<SetStateAction<DepositDetailsModel | null>>;
  onSubmit: () => void;
  isLoading?: boolean;
}

const ProfileDetailsPopup = ({
  open,
  onClose,
  onSubmit,
  playerDetails,
  setPlayerDetails
}: IProps) => {
  const { t } = useTranslation();
  const [formErrors, setFormErrors] = useState(defaultErrorsObj);

  const [cookies] = useCookies(['country', 'language']);

  const cookieCountryValue = cookies?.country;
  const cookieCountry = useMemo(() => {
    return countries.find(
      (c) => c.label.toLowerCase() === cookieCountryValue?.toLowerCase()
    ) as CountryType;
  }, [cookieCountryValue]);
  const [phoneCountry, setPhoneCountry] = useState<CountryType | null>(cookieCountry || null);
  const [country, setCountry] = useState<CountryType | null>(cookieCountry || null);

  const keyPress = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      onSubmit();
    }
  };

  useEffect(() => {
    if (phoneCountry !== null) {
      setPlayerDetails({
        ...playerDetails,
        country: phoneCountry.label
      });
    }
  }, [phoneCountry]);

  const handleChange = (
    name: string,
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { value } = event.target;
    setPlayerDetails({ ...playerDetails, [name]: value });
  };

  const handlePhoneCodeChange = (_: unknown, value: CountryType | null) => {
    if (value === null) {
      setFormErrors({ ...formErrors, phoneCode: t('errors.signup.phoneCode') });
    } else {
      setFormErrors({ ...formErrors, phoneCode: null });
    }
    setPhoneCountry(value);
  };

  const handleCountryChange = (_: unknown, value: CountryType | null) => {
    if (value === null) {
      setFormErrors({ ...formErrors, country: t('errors.signup.country') });
    } else {
      setPlayerDetails({
        ...playerDetails,
        country: (value?.label as string).replace(/\s/g, '')
      });
      setFormErrors({ ...formErrors, country: null });
    }
    setCountry(value);
  };

  const isFormValid = () => {
    return (
      playerDetails.firstName.length > 2 &&
      playerDetails.lastName.length > 2 &&
      playerDetails.address.length > 2 &&
      playerDetails.city.length > 2 &&
      playerDetails.postCode.length > 2 &&
      playerDetails.phoneNumber.length > 4 &&
      phoneCountry !== null
    );
  };

  return (
    <Dialog
      open={open}
      TransitionComponent={Transition}
      keepMounted
      aria-describedby="alert-dialog-slide-description"
      classes={{ paper: 'mui-dialog-details-popup' }}
      onKeyDown={keyPress}
      onClose={onClose}
    >
      <Box className="user-details-header">
        <Box className="user-details-header__title">{t('account.userDetailsPopupTitle')}</Box>
        <IconButton aria-label="close" onClick={onClose} className="close-icon-button">
          <Close />
        </IconButton>
      </Box>
      <Box className="user-details-content-container">
        <Box className="user-details-content-container__content">
          <Box className="user-details-content-container__content__input">
            <InputLabel htmlFor="firstName" style={labelStyle}>
              {t('account.firstNameLabel')}
            </InputLabel>
            <TextField
              id="firstName"
              name="firstName"
              margin="normal"
              variant="outlined"
              fullWidth
              required
              value={playerDetails.firstName}
              onChange={(event) => handleChange(event.target.name, event)}
              error={formErrors.firstName !== null}
              helperText={formErrors.firstName ? formErrors.firstName : ''}
              sx={{
                height: '46px',
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: getBorderColor(playerDetails.firstName)
                  }
                }
              }}
              inputProps={{ sx: inputPropsStyle }}
              placeholder={t('account.namePlaceHolder') as string}
              color="info"
            />
          </Box>
          <Box className="user-details-content-container__content__input">
            <InputLabel htmlFor="lastName" style={labelStyle}>
              {t('account.lastNameLabel')}
            </InputLabel>
            <TextField
              id="lastName"
              name="lastName"
              margin="normal"
              variant="outlined"
              fullWidth
              required
              value={playerDetails.lastName}
              onChange={(event) => handleChange(event.target.name, event)}
              error={formErrors.lastName !== null}
              helperText={formErrors.lastName ? formErrors.lastName : ''}
              sx={{
                height: '46px',
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: getBorderColor(playerDetails.lastName)
                  }
                }
              }}
              inputProps={{ sx: inputPropsStyle }}
              placeholder={t('account.namePlaceHolder') as string}
              color="info"
            />
          </Box>
        </Box>
        <Box>
          <InputLabel htmlFor="phone" style={labelStyle}>
            {t('account.phoneLabel')}
          </InputLabel>
          <TextField
            id="phone"
            name="phoneNumber"
            margin="normal"
            variant="outlined"
            fullWidth
            required
            value={playerDetails.phoneNumber}
            onChange={(event) => handleChange(event.target.name, event)}
            error={formErrors.phoneCode !== null}
            helperText={formErrors.phoneCode ? formErrors.phoneCode : ''}
            sx={{
              height: '46px',
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: getBorderColor(playerDetails.phoneNumber)
                }
              }
            }}
            inputProps={{ sx: inputPropsStyle }}
            placeholder={t('account.phonePlaceHolder') as string}
            color="info"
            InputProps={{
              startAdornment: (
                <Autocomplete
                  id="phoneCodeAutocomplate"
                  filterOptions={filterOptions}
                  style={{
                    position: 'relative',
                    height: 30,
                    top: '-24px',
                    left: '-14px',
                    width: '43%'
                  }}
                  disableClearable
                  PaperComponent={CustomPaper}
                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  //@ts-ignore
                  value={phoneCountry}
                  onChange={handlePhoneCodeChange}
                  options={countries}
                  getOptionLabel={(option) => `+${option.phone}`}
                  renderOption={(props, option) => (
                    <Box
                      component="li"
                      sx={{
                        height: '46px',
                        '& > img': { mr: 2, flexShrink: 0 },
                        '& .MuiInputBase - input': {
                          height: '46px'
                        }
                      }}
                      {...props}
                      key={option.label}
                    >
                      <img
                        loading="lazy"
                        width="20"
                        src={`https://flagcdn.com/w20/${option.code.toLowerCase()}.png`}
                        srcSet={`https://flagcdn.com/w40/${option.code.toLowerCase()}.png 2x`}
                        alt="countryFlag"
                      />
                      {option.label} +{option.phone}
                    </Box>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      id="phoneCodeInput"
                      name="phoneCodeInput"
                      margin="normal"
                      variant="standard"
                      error={formErrors.phoneCode !== null}
                      helperText={formErrors.phoneCode ? formErrors.phoneCode : ''}
                      sx={{
                        borderRight: (t) => `1px solid ${t.palette.grey[50]}`,
                        height: '46px',
                        '& .MuiOutlinedInput-root': {
                          height: '46px',
                          padding: '0 !important',
                          '& fieldset': {
                            // borderColor: getBorderColor(country?.label)
                          }
                        }
                      }}
                      color="info"
                      inputProps={{
                        ...params.inputProps,
                        sx: {
                          ...inputPropsStyle,
                          textAlign: 'right',
                          marginRight: '3%',
                          color: (t: Theme) => t.palette.info.main
                        }
                      }}
                      InputProps={{
                        ...params.InputProps,
                        disableUnderline: true
                      }}
                    />
                  )}
                />
              )
            }}
          />
        </Box>
        <Box className="user-details-content-container__content__input">
          <InputLabel htmlFor="address" style={labelStyle}>
            {t('account.addressLabel') as string}
          </InputLabel>
          <TextField
            id="address"
            name="address"
            margin="normal"
            variant="outlined"
            fullWidth
            required
            value={playerDetails.address}
            onChange={(event) => handleChange(event.target.name, event)}
            error={formErrors.address !== null}
            helperText={formErrors.address ? formErrors.address : ''}
            sx={{
              height: '46px',
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: getBorderColor(playerDetails.address)
                }
              }
            }}
            inputProps={{ sx: inputPropsStyle }}
            placeholder={t('account.addressPlaceHolder') as string}
            color="info"
          />
        </Box>
        <Box>
          <InputLabel htmlFor="countryAutocomplate" style={labelStyle}>
            {t('account.countryLabel')}
          </InputLabel>
          <Autocomplete
            id="countryAutocomplate"
            fullWidth
            value={country}
            onChange={handleCountryChange}
            options={countries}
            getOptionLabel={(option) => option.label}
            renderOption={(props, option) => (
              <Box
                component="li"
                sx={{
                  height: '46px',
                  '& > img': { mr: 2, flexShrink: 0 },
                  '& .MuiInputBase - input': {
                    height: '46px'
                  }
                }}
                {...props}
              >
                <img
                  loading="lazy"
                  width="20"
                  src={`https://flagcdn.com/w20/${option.code.toLowerCase()}.png`}
                  srcSet={`https://flagcdn.com/w40/${option.code.toLowerCase()}.png 2x`}
                  alt="countryFlag"
                />
                {option.label}
              </Box>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                id="countryInput"
                name="countryInput"
                margin="normal"
                variant="outlined"
                error={formErrors.country !== null}
                helperText={formErrors.country ? formErrors.country : ''}
                sx={{
                  height: '46px',
                  '& .MuiOutlinedInput-root': {
                    height: '46px',
                    padding: '0 !important',
                    '& fieldset': {
                      borderColor: getBorderColor(country?.label)
                    }
                  }
                }}
                placeholder={t('account.countryPlaceHolder') as string}
                color="info"
                inputProps={{
                  ...params.inputProps,
                  sx: inputPropsStyle
                  // autoComplete: 'new-password' // disable autocomplete and autofill
                }}
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <InputAdornment position="start" style={{ paddingLeft: '13px' }}>
                      <Language sx={{ fill: getIconColor(country?.label, formErrors.country) }} />
                    </InputAdornment>
                  )
                }}
              />
            )}
          />
        </Box>
        <Box className="user-details-content-container__content__input">
          <InputLabel htmlFor="city" style={labelStyle}>
            {t('account.cityLabel') as string}
          </InputLabel>
          <TextField
            id="city"
            name="city"
            margin="normal"
            variant="outlined"
            fullWidth
            required
            value={playerDetails.city}
            onChange={(event) => handleChange(event.target.name, event)}
            error={formErrors.city !== null}
            helperText={formErrors.city ? formErrors.city : ''}
            sx={{
              height: '46px',
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: getBorderColor(playerDetails.city)
                }
              }
            }}
            inputProps={{ sx: inputPropsStyle }}
            placeholder={t('account.cityPlaceHolder') as string}
            color="info"
          />
        </Box>
        <Box className="user-details-content-container__content__input">
          <InputLabel htmlFor="postCode" style={labelStyle}>
            {t('account.postCodeLabel') as string}
          </InputLabel>
          <TextField
            id="postCode"
            name="postCode"
            margin="normal"
            variant="outlined"
            fullWidth
            required
            value={playerDetails.postCode}
            onChange={(event) => handleChange(event.target.name, event)}
            error={formErrors.postCode !== null}
            helperText={formErrors.postCode ? formErrors.postCode : ''}
            sx={{
              height: '46px',
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: getBorderColor(playerDetails.postCode)
                }
              }
            }}
            inputProps={{ sx: inputPropsStyle }}
            placeholder={t('account.postCodePlaceHolder') as string}
            color="info"
          />
        </Box>
      </Box>
      <Box>
        <Box>
          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={!isFormValid()}
            className="continue-button-container__custom-continue-button"
            onClick={onSubmit}
          >
            {t('account.userDetailsPopupSubmitButton')}
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
};

export default ProfileDetailsPopup;
